// @flow

import { fromPairs, toPairs } from "ramda";

import parserExpressao from "../util/expression";

type Expressao =
  | {
      type: "BINARY_EXPRESSION",
      operator: "+" | "-" | "*" | "/" | "^" | "#",
      left: Expressao,
      right: Expressao,
    }
  | { type: "UNARY_EXPRESSION", operator: "!", value: Expressao }
  | { type: "FUNCTION", name: string, arguments: Array<Expressao> }
  | { type: "NUMBER", value: string }
  | { type: "CONSTANT", value: string }
  | { type: "REFERENCE", value: string };

export const parseiaExpressao = (expressao: string): ?Expressao => {
  try {
    return parserExpressao.parse(expressao.trim());
  } catch {
    return null;
  }
};

export const parseiaExpressaoPorAtivo = (expressao: string): string =>
  expressao.replace(/\[[a-zA-Z_0-9]+:"[^"]+"\]/g, "");

export const referenciasDaExpressao = (expressao: Expressao): Array<string> => {
  const extraiReferencias = (
    refs: Array<string>,
    expr: Expressao
  ): Array<string> => {
    switch (expr.type) {
      case "BINARY_EXPRESSION":
        return [
          ...refs,
          ...referenciasDaExpressao(expr.left),
          ...referenciasDaExpressao(expr.right),
        ];
      case "UNARY_EXPRESSION":
        return [...refs, ...referenciasDaExpressao(expr.value)];
      case "FUNCTION":
        return [
          ...refs,
          ...expr.arguments.flatMap(arg => referenciasDaExpressao(arg)),
        ];
      case "REFERENCE":
        return [...refs, expr.value];
      default:
        return refs;
    }
  };

  return extraiReferencias([], expressao);
};

type ExpressaoRanking = {
  funcao: "ranking" | "indice" | "percentil",
  pesos: { [string]: number },
};

export const parseiaExpressaoRanking = (
  expressao: string
): ?ExpressaoRanking => {
  const expr = parseiaExpressao(expressao);
  if (!expr) return null;

  const extraiNumero = expr => {
    switch (expr.type) {
      case "NUMBER":
        return parseFloat(expr.value);
      default:
        return null;
    }
  };

  const extraiAlias = expr => referenciasDaExpressao(expr)[0];

  const extraiPesos = (pesos, expr) => {
    switch (expr.type) {
      case "BINARY_EXPRESSION":
        switch (expr.operator) {
          case "+":
          case "-":
            return [
              ...pesos,
              ...extraiPesos(pesos, expr.left),
              ...extraiPesos(pesos, expr.right),
            ];

          case "*":
            return [
              ...pesos,
              {
                alias: extraiAlias(expr.left) || extraiAlias(expr.right),
                peso: extraiNumero(expr.left) || extraiNumero(expr.right) || 0,
              },
            ];

          default:
            return pesos;
        }

      default:
        return pesos;
    }
  };

  switch (expr.type) {
    case "FUNCTION": {
      const funcao = expr.name.toLowerCase();
      switch (funcao) {
        case "ranking":
        case "indice":
        case "percentil":
          return {
            funcao,
            pesos: fromPairs(
              extraiPesos([], expr.arguments[0]).map(({ alias, peso }) => [
                alias,
                peso,
              ])
            ),
          };

        default:
          return null;
      }
    }

    default:
      return null;
  }
};

export const printaExpressaoRanking = ({
  funcao,
  pesos,
}: ExpressaoRanking): string => {
  const argumentos = toPairs(pesos)
    .map(([medida, peso]) => `(${peso} * ${medida})`)
    .join(" + ");

  return `${funcao}(${argumentos})`;
};
